import { Head, Link, router, usePage } from "@inertiajs/react";
import { ArrowLeft, FileText, Globe, Hash, Package, Tag } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import AliasManager from "@/components/alias-manager";
import InlineEditableCategorySubcategory from "@/components/inline-editable-category-subcategory";
import InlineEditableField from "@/components/inline-editable-field";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import { shareOrganizationProductPath } from "@/routes";
import type { BreadcrumbItem, Product, SharedData } from "@/types";

interface ProductShowProps {
  product: Product;
}

export default function ProductShow({ product }: ProductShowProps) {
  const routes = useOrganizationRoutes();
  const { auth } = usePage<SharedData>().props;
  const currentOrganizationId = auth.current_organization?.id ?? null;
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const productsHref = routes.products.index();
  const productHref = routes.products.show(product);
  const shareHref = shareOrganizationProductPath(routes.organizationSlug, product);
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Productos",
      href: productsHref,
    },
  ];
  const breadcrumbsWithCurrent: BreadcrumbItem[] = [
    ...breadcrumbs,
    {
      title: product.normalized_name,
      href: "#",
    },
  ];
  const canShare =
    product.visibility === "private" &&
    product.owner_organization_id === currentOrganizationId;

  const handleShare = () => {
    setIsSharing(true);
    router.patch(shareHref, undefined, {
      onFinish: () => {
        setIsSharing(false);
        setIsShareDialogOpen(false);
      },
      onError: () => {
        toast.error("No se pudo compartir el producto");
      },
    });
  };

  return (
    <AppLayout breadcrumbs={breadcrumbsWithCurrent}>
      <Head title={`Producto: ${product.normalized_name}`} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button variant="ghost" size="sm" asChild className="mb-3">
              <Link href={productsHref}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver a Productos
              </Link>
            </Button>
            <h1 className="text-3xl font-bold">
              {product.normalized_name}
            </h1>
            <p className="text-muted-foreground">
              Gestiona la información del producto
            </p>
          </div>
          {canShare && (
            <Button onClick={() => setIsShareDialogOpen(true)}>
              <Globe className="mr-2 h-4 w-4" />
              Compartir producto
            </Button>
          )}
        </div>

        {/* General Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Información General
            </CardTitle>
            <CardDescription>
              Haz clic en el ícono de lápiz para editar cualquier campo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InlineEditableField
                label="Nombre"
                value={product.normalized_name}
                field="normalized_name"
                updateUrl={productHref}
                icon={FileText}
                placeholder="Nombre del producto"
              />
              <InlineEditableField
                label="Código Interno"
                value={product.internal_code}
                field="internal_code"
                updateUrl={productHref}
                icon={FileText}
                placeholder="Código interno"
              />
            </div>

            <Separator className="my-6" />

            <InlineEditableField
              label="Descripción"
              value={product.description}
              field="description"
              type="textarea"
              updateUrl={productHref}
              icon={FileText}
              placeholder="Agregar descripción..."
            />
          </CardContent>
        </Card>

        {/* Category/Subcategory */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Categorización
            </CardTitle>
            <CardDescription>
              Edita la categoría y subcategoría del producto
            </CardDescription>
          </CardHeader>
          <CardContent>
            <InlineEditableCategorySubcategory
              organizationSlug={routes.organizationSlug}
              categoryId={product.category_id ?? null}
              subcategoryId={product.subcategory_id ?? null}
              category={product.category}
              subcategory={product.subcategory}
              updateUrl={productHref}
            />
          </CardContent>
        </Card>

        {/* Aliases Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5" />
              Alias del Producto
            </CardTitle>
            <CardDescription>
              Agrega diferentes formas en que proveedores y solicitantes pueden
              nombrar este producto. La IA usa estos alias para hacer
              coincidencias automáticas precisas.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AliasManager
              aliases={product.aliases ?? []}
              addAliasUrl={routes.products.addAlias(product.id)}
              removeAliasUrl={routes.products.removeAlias(product.id)}
            />
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Compartir producto públicamente</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Una vez compartido, este producto será visible para todas las
                organizaciones que tengan habilitado el catálogo público.
              </p>
              <p>
                Ya no podrás editarlo directamente. Cualquier cambio deberá
                hacerse como override local.
              </p>
              <p className="font-medium text-amber-600 dark:text-amber-400">
                Esta acción es irreversible.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSharing}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleShare} disabled={isSharing}>
              {isSharing ? "Compartiendo..." : "Compartir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
